import React, { Suspense, lazy } from "react";
import { withUserProfile } from "../../../../../../userProfile/userProfileContext";
import { withStyles } from "@material-ui/core";
import { observer, inject } from "mobx-react";
import { autorun } from "mobx";
import { compose } from "recompose";
import DataGrid from '@dataGrid';
import GridConfig from "./GridConfig";
import styles from "./../styles";
import { csvFormulaInjectionSanitizer } from "../../../../../../utilities/csvUtils";
import StatusBar from '@dataGrid/components/StatusBar';
import Header from "../GridHeader";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../../../utils";
import { withAppConfig } from "../../../../../../appConfigContext";
import { isEqual } from "lodash/lang";
import { AppLoading } from "../../../../../../loaders/loaders";
import Events from "../../../../../../utilities/EventEmitter";
import {
  getShipmentsPrintGroupOrder,
  getShipmentsPrintSectionHeaderDetails,
  getUniqueKeyForStripPriorityRowData,
} from "../../utils";

import { PrintReport } from "../../../../../common";
import { getDisplayedRowData } from "../../../../../common/PrintReport/utils/reportDataFormatter";
const ProImages = lazy(() => import("@ProImages"));
import { STRIP_SHIPMENTS_PRINT_TITLE } from "../../constants";

@inject("IBPlanningStore")
@observer
class Shipments extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig({
      columnConfig: this.props.configJSON?.gridColumnConfig,
      userConfig: this.props.userData.userProfile?.columnPref
    });
    this.state = {
      proIdList: [],
      showProImagesPopup: false,
      quickFilterText: "",
      selectedTrailers: [],
      trailerDisplayOrder: [],
    };
    this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
    this.gridContext = {
      hasProImagesURL: this.hasProImagesURL(),
      specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
    };
    this.gridApi = {};
    this.printRef = React.createRef(null);
    this.beforePrintFn = null;
  }
  onGridReady = ({ api }) => {
    this.gridApi = api;
  };

  componentDidMount() {
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.serviceLevelList,
          this.props.IBPlanningStore.serviceLevelList
        )
      ) {
        this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
        this.gridContext = {
          hasProImagesURL: this.hasProImagesURL(),
          specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
        };
      }
    });
    Events.on("UpdateSelectedTrailersShipments", this.updateTrailersShipments);
  }

  componentWillUnmount() {
    this?.autorunPriorityShipmentList();
    Events.off("UpdateSelectedTrailersShipments", this.updateTrailersShipments);
  }

  updateTrailersShipments = ({ selectedTrailers, trailerDisplayOrder }) => {
    this.setState({
      selectedTrailers,
      trailerDisplayOrder,
    });
  };

  getTrailersShipments = () => {
    return (
      this.props.shipmentsData?.filter((shipment) => {
        const { trailerNumber, trailerScac } = shipment;
        return this.state.selectedTrailers.includes(
          getUniqueKeyForStripPriorityRowData(trailerNumber, trailerScac)
        );
      }) ?? []
    );
  };

  updateQuickFilterText = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  hasProImagesURL = () => {
    return Boolean(this.props.configJSON.proImagesUrl);
  };

  exportCSV = () => {
    if (!this.props.shipmentsData?.length || !this.gridApi) {
      return;
    }
    const columnIds = this.gridApi.getColumns()
      .reduce((columnKeys, column) => {
        if (column.getColDef().headerName) {
          columnKeys.push(column.getColId());
        }
        return columnKeys;
      }, []);
    const params = {
      fileName: "Strip_Priority_Report",
      columnKeys: columnIds,
      processCellCallback: (cell) => csvFormulaInjectionSanitizer(cell.value),
    };
    this.gridApi.exportDataAsCsv(params);
  };

  onCellClicked = (params) => {
    this.setState({
      proIdList: this.gridApi
        .getSelectedRows()
        .map((shipment) => shipment.proId),
    });
    if (
      this.hasProImagesURL() &&
      params.column.colId === "proId" &&
      this.state.proIdList.length === 1
    ) {
      this.updateModalVisibility(true);
    }
  };

  updateModalVisibility = (showProImagesPopup) => {
    this.setState({
      showProImagesPopup,
    });
  };

  constructStatusBar = (filteredData) => {
    const statusBarData = [
      {
        label: `${filteredData.length || 0} Total Shipments`,
        filterCondition: "",
      },
    ];
    return statusBarData;
  };

  setPrintRef = (compRef, beforePrintFn) => {
    this.printRef = compRef;
    this.beforePrintFn = beforePrintFn;
  };

  getPrintDataFn = () => {
    return getDisplayedRowData({
      groupPrintSections: {
        pageBreakBetweenSections: true,
        getGroupKey: (shipment) =>
          getUniqueKeyForStripPriorityRowData(
            shipment.trailerNumber,
            shipment.trailerScac
          ),
        // Shipments sort by route/stop; print sections follow Trailers grid order (all or selected).
        groupOrder: getShipmentsPrintGroupOrder({
          selectedTrailers: this.state.selectedTrailers,
          trailerDisplayOrder: this.state.trailerDisplayOrder,
        }),
        getSectionHeaderProps: ({ rows }) => ({
          title: STRIP_SHIPMENTS_PRINT_TITLE,
          headerDetails: getShipmentsPrintSectionHeaderDetails(rows),
        }),
      },
    });
  };

  render() {
    const { classes, toggleMaximize, isMaximized } = this.props;
    const { config } = this.gridConfig;
    const { shipmentsData } = this.props;
    const selectedTrailersShipments = this.getTrailersShipments();
    return (
      <>
      <div
        className={classes.shipmentContainer}
      >
        <Header
          exportCSV={this.exportCSV}
          title="Shipments"
          updateQuickFilterText={this.updateQuickFilterText}
          showPrint
          gridApi={this.gridApi}
          onBeforeGetContent={this.beforePrintFn}
          printRef={this.printRef}
          toggleMaximize={toggleMaximize}
          isMaximized={isMaximized}
          userProfile={this.props.userData.userProfile}
        />
        <div className={`${classes.dataGrid} ${isMaximized ? '' : classes.shipmentGridHeight}`}>
          <DataGrid
            columnDefs={config.columns}
            config={config}
            rowData={
              selectedTrailersShipments.length === 0
                ? shipmentsData
                : selectedTrailersShipments
            }
            enableSorting={config.enableSorting}
            onGridReady={this.onGridReady}
            quickFilterText={this.state.quickFilterText}
            rowSelection={this.gridConfig.config.rowSelection}
            rowDeselection={this.gridConfig.config.rowDeselection}
            onCellClicked={this.onCellClicked}
            context={this.gridContext}
            showErrorMessage={this.props.stripPriorityGridStatus}
            panelLabel={(filteredData) => (
              <StatusBar data={this.constructStatusBar(filteredData)} />
            )}
          />
          <Suspense fallback={<AppLoading />}>
            {this.state.showProImagesPopup && (
              <ProImages
                proNumber={this.state.proIdList[0]}
                show={this.state.showProImagesPopup}
                onClose={() => {
                  this.updateModalVisibility(false);
                }}
              />
            )}
          </Suspense>
        </div>
      </div>
      <PrintReport
        gridApi={this.gridApi}
        setPrintRef={this.setPrintRef}
        getPrintDataFn={this.getPrintDataFn}
      />
      </>
    );
  }
}
export default compose(
  withStyles(styles),
  withUserProfile,
  withAppConfig
)(Shipments);
